import { organizationModule } from '@/store/organization/module'
import { isSupportWindow } from '@/utils'
import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { analyticsModule } from '@/store/analytics/module'
import { userModule } from '@/store/user/module'
import { Experiment, type ExperimentConfig } from '@amplitude/experiment-js-client'

const initAmplitude = async () => {
    const amplitudeApiKey = import.meta.env.VUE_APP_AMPLITUDE_API_KEY
    if (amplitudeApiKey && !isSupportWindow()) {
        const ampliInitConfig: amplitude.Types.BrowserOptions = {
            defaultTracking: {
                attribution: {
                    /**
                     * See ampli docs here
                     * https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#exclude-referrers
                     *  */
                    excludeReferrers: [],
                },
            },
        }
        const customAmpliServerUrl = import.meta.env.VUE_APP_AMPLITUDE_SERVER_URL
        if (customAmpliServerUrl) {
            ampliInitConfig.serverUrl = customAmpliServerUrl
        }

        // Instrument amplitude with session replay tracking
        const sessionReplayTracking = sessionReplayPlugin({
            sampleRate: 1, // 100% sample rate as indicated in ticket
        })
        amplitude.add(sessionReplayTracking)

        amplitude.init(amplitudeApiKey, ampliInitConfig)

        const ampliExperimentConfig: ExperimentConfig = {}
        const customAmpliExperimentUrl = import.meta.env.VUE_APP_AMPLITUDE_EXPERIMENT_URL
        if (customAmpliExperimentUrl) {
            ampliExperimentConfig.serverUrl = customAmpliExperimentUrl
        }
        const customAmpliFlagUrl = import.meta.env.VUE_APP_AMPLITUDE_FLAG_URL
        if (customAmpliFlagUrl) {
            ampliExperimentConfig.flagsServerUrl = customAmpliFlagUrl
        }
        const experiment = Experiment.initializeWithAmplitudeAnalytics(amplitudeApiKey, ampliExperimentConfig)
        analyticsModule.state.experiment = experiment
        await experiment.start()
    }
}

const amplitudeTrack = (
    eventName: string,
    eventProperties?: Record<string, string | number | boolean | string[] | undefined>
) => {
    const amplitudeApiKey = import.meta.env.VUE_APP_AMPLITUDE_API_KEY
    if (amplitudeApiKey && !isSupportWindow()) {
        const currentOrganization = organizationModule.getters.getCurrentOrganization()
        const eventPropsWithOrgData = currentOrganization
            ? {
                ...eventProperties,
                organizationId: currentOrganization.objectId,
                organizationName: currentOrganization.name,
            }
            : eventProperties

        amplitude.track(eventName, eventPropsWithOrgData)
    }
}

const amplitudeReset = () => {
    const amplitudeApiKey = import.meta.env.VUE_APP_AMPLITUDE_API_KEY
    if (amplitudeApiKey && !isSupportWindow()) {
        amplitude.reset()
    }
}

const amplitudeFetchVariant = async (flagKey: string) => {
    const amplitudeApiKey = import.meta.env.VUE_APP_AMPLITUDE_API_KEY
    const experiment = analyticsModule.state.experiment
    if (amplitudeApiKey && !isSupportWindow() && experiment) {
        const user = userModule.state.user
        if (user) {
            await experiment.fetch({
                user_id: user.objectId,
                user_properties: {
                    email: user.username,
                    createdAt: user.createdAt,
                },
            }, {
                flagKeys: [ flagKey ],
            })
        } else {
            await experiment.fetch(undefined, { flagKeys: [ flagKey ] })
        }
        const variant = experiment.variant(flagKey)
        return variant
    }
}

const setAmplitudeUser = ({ id, email, createdAt, currentOrgId }: {
    id: string
    email: string
    createdAt: string   // ISO date string
    currentOrgId?: string
}) => {
    const amplitudeApiKey = import.meta.env.VUE_APP_AMPLITUDE_API_KEY
    if (amplitudeApiKey && !isSupportWindow()) {
        amplitude.setUserId(id)

        const identifyEvent = new amplitude.Identify()
        identifyEvent.set('email', email)
        identifyEvent.set('createdAt', createdAt)
        if (currentOrgId) {
            identifyEvent.set('currentOrganization', currentOrgId)
            amplitude.setGroup('Organization', currentOrgId)
        }

        amplitude.identify(identifyEvent)
    }
}

export default {
    initAmplitude,
    amplitudeTrack,
    amplitudeReset,
    amplitudeFetchVariant,
    setAmplitudeUser,
}
